import { create } from 'zustand';
import { supabase } from '../lib/supabase';

interface AiModuleState {
  aiModuleEnabled: boolean;
  loading: boolean;
  initialized: boolean;
  fetchAiModuleStatus: (tenantId: string) => Promise<void>;
}

const TTL_MS = 5 * 60 * 1000;
const cacheKey = (t: string) => `ai_module_enabled:${t}`;

const readCache = (tenantId: string): { enabled: boolean; fresh: boolean } | null => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const raw = localStorage.getItem(cacheKey(tenantId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      enabled: parsed.enabled === true,
      fresh: typeof parsed.fetchedAt === 'number' && Date.now() - parsed.fetchedAt < TTL_MS,
    };
  } catch {
    return null;
  }
};

const writeCache = (tenantId: string, enabled: boolean) => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    localStorage.setItem(cacheKey(tenantId), JSON.stringify({ enabled, fetchedAt: Date.now() }));
  } catch {}
};

let inFlight: Promise<void> | null = null;
let inFlightTenantId: string | null = null;

export const useAiModuleStore = create<AiModuleState>((set) => ({
  aiModuleEnabled: false,
  loading: false,
  initialized: false,
  fetchAiModuleStatus: async (tenantId: string) => {
    if (!tenantId) return;

    // 1+2: hidratar do cache e pular fetch se fresco
    const cached = readCache(tenantId);
    if (cached) {
      set({ aiModuleEnabled: cached.enabled, initialized: true, loading: !cached.fresh });
      if (cached.fresh) return;
    } else {
      set({ loading: true });
    }

    // 3: dedup se já existe uma requisição em voo para o mesmo tenant
    if (inFlight && inFlightTenantId === tenantId) {
      return inFlight;
    }

    inFlightTenantId = tenantId;
    inFlight = (async () => {
      try {
        const { data, error } = await supabase
          .from('crm_tenants')
          .select('ai_module_enabled, follow_module_enabled')
          .eq('id', tenantId)
          .maybeSingle();

        if (error) {
          throw error;
        }

        const isEnabled = data?.ai_module_enabled === true || (data as any)?.follow_module_enabled === true;
        writeCache(tenantId, isEnabled);
        set({ aiModuleEnabled: isEnabled, loading: false, initialized: true });
      } catch (e: any) {
        // 4: NÃO zerar — manter valor anterior/cacheado
        console.warn('[AI_MODULE] falha ao checar flag; mantendo estado atual:', e?.message || e);
        set({ loading: false, initialized: true });
      } finally {
        inFlight = null;
        inFlightTenantId = null;
      }
    })();

    return inFlight;
  },
}));

